import {
  Box,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import NumberField from 'components/number-field';
import { TFunction } from 'i18next';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { formatCurrencyValueToNumber } from 'utils/helpers';
import DatePicker from 'components/date-picker/date-picker';

import { ProrationsForm } from '../types';
import { parseISO, isValid, isBefore, isAfter } from 'date-fns';
import { AlertAdvice } from 'components/common/alert-advice';

type FormProrationProps = {
  t: TFunction,
  selectedTabIndex: number,
  isLoading: boolean
};

const useStyles = makeStyles((theme: any) => ({
  subTitle: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
    marginBottom: theme.spacing(1)
  },
  bottomContainer: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  bottomInnerContainer: {
    padding: theme.spacing(3),
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: theme.spacing(0.5),
    borderColor: theme.accent.main,
  },
  buttonOption: { textTransform: 'capitalize' },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(2.5),
    '@media (max-width: 599.95px)': {
      flexDirection: 'column',
      gap: theme.spacing(2),
      '& > *': {
        width: '100%',
      },
    },
  },
}));

export const FormProration = ({
  t,
  selectedTabIndex,
  isLoading
}: FormProrationProps) => {
  const classes = useStyles();
  const { control } = useFormContext<ProrationsForm>();
  const { forms } = useWatch({ control });
  const prorateUsing = forms ? forms[selectedTabIndex].kind : "installment";
  let showProrationDateWarning = false;
  let perDiemDecimals = 4;

  if (prorateUsing === "per_diem" && forms) {
    const {
      proration_date,
      start_date,
      end_date,
      decimals
    } = forms[selectedTabIndex];
    showProrationDateWarning = !(
      proration_date &&
      start_date &&
      end_date &&
      isValid(parseISO(proration_date)) &&
      isValid(parseISO(start_date)) &&
      isValid(parseISO(end_date)) &&
      isAfter(parseISO(proration_date), parseISO(start_date)) &&
      isBefore(parseISO(proration_date), parseISO(end_date))
    );
    perDiemDecimals = decimals!;
  }

  return (
    <Box>
      <Typography variant="h6" className={classes.subTitle}>
        {t('taxesAndProrations:proration')}
      </Typography>

      <Box display="flex" style={{ gap: "20px" }} flexDirection="column">
        <AlertAdvice
          show={showProrationDateWarning}
          message={t('orders:prorationDateWarningMessage')}
          severity="warning"
        />
        <Box className={classes.fieldRow}>
          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.start_date`}
            name={`forms.${selectedTabIndex}.start_date`}
            render={({ field }) => (
              <DatePicker
                name="start_date"
                disableToolbar={false}
                label={prorateUsing === "per_diem" ? t('orders:periodStartDateLabel') : t('taxesAndProrations:installment-start-date')}
                value={field.value}
                handleBlur={field.onBlur}
                onChange={(date) => field.onChange(date)}
                disabled={isLoading}
              />
            )}
          />

          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.proration_date`}
            name={`forms.${selectedTabIndex}.proration_date`}
            render={({ field }) => (
              <DatePicker
                name="proration_date"
                disableToolbar={false}
                label={t('taxesAndProrations:proration-date')}
                value={field.value}
                handleBlur={field.onBlur}
                onChange={(date) => field.onChange(date)}
                disabled={isLoading}
              />
            )}
          />

          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.end_date`}
            name={`forms.${selectedTabIndex}.end_date`}
            render={({ field }) => (
              <DatePicker
                name="stop_date"
                label={prorateUsing === "per_diem" ? t('orders:periodEndDateLabel') : t('taxesAndProrations:installment-end-date')}
                disableToolbar={false}
                value={field.value}
                handleBlur={field.onBlur}
                onChange={(date) => field.onChange(date)}
                disabled={isLoading}
              />
            )}
          />

        </Box>

        <Box className={classes.fieldRow}>
          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.perDiemAmount.amount`}
            name={`forms.${selectedTabIndex}.perDiemAmount.amount`}
            render={({ field }) => {
              return (
                <NumberField
                  color="secondary"
                  label={t('orders:perDiemLabel')}
                  name="perDiem"
                  handleBlur={field.onBlur}
                  value={field.value}
                  thousandSeparator
                  decimalScale={perDiemDecimals}
                  onValueChange={({ floatValue }) => field.onChange(floatValue ?? null)}
                  prefix="$"
                  disabled
                />
              );
            }}
          />

          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.days`}
            name={`forms.${selectedTabIndex}.days`}
            render={({ field }) => (
              <NumberField
                disabled
                name="number_of_days_in_period"
                label={t('taxesAndProrations:number-of-days-in-period')}
                isAllowed={({ floatValue = 0 }) => floatValue > -1 && floatValue < 367}
                value={field.value}
                handleBlur={field.onBlur}
                onChange={(event) => {
                  const value = formatCurrencyValueToNumber(event.target.value, 0, null, null) as number;
                  field.onChange(value);
                }}
              />
            )}
          />

          <Controller
            control={control}
            key={`forms.${selectedTabIndex}.days_year`}
            name={`forms.${selectedTabIndex}.days_year`}
            render={({ field }) => (
              <NumberField
                disabled
                name="days_year"
                label={t("taxesAndProrations:days-year")}
                isAllowed={({ floatValue = 0 }) => floatValue > -1 && floatValue < 367}
                value={field.value}
                handleBlur={field.onBlur}
                onChange={(event) => {
                  const value = formatCurrencyValueToNumber(event.target.value, 0, null, null) as number;
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default FormProration;
